import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiEdit3, FiSave } from 'react-icons/fi';
import apiService from '../../services/api';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import { DateTime } from 'luxon';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-primary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background-color: var(--background-primary);
  color: var(--text-primary);
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  background-color: var(--background-primary);
  color: var(--text-primary);
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TimeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;


const TimezoneIndicator = styled.div`
  background-color: rgba(96, 165, 250, 0.1);
  border: 1px solid var(--info-color);
  border-radius: 0;
  padding: 0.75rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const TimezoneText = styled.div`
  font-size: 0.875rem;
  color: var(--info-color);
  font-weight: 500;
`;



const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &.secondary {
    background-color: var(--background-primary);
    color: var(--text-primary);
    
    &:hover:not(:disabled) {
      background-color: var(--background-secondary);
    }
  }
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-hover);
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  border-radius: 0;
  padding: 0.75rem;
  color: var(--error-color);
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function EditAttendanceModal({ isOpen, onClose, session, onSessionUpdated }) {
  const [formData, setFormData] = useState({
    date: '',
    title: '',
    notes: '',
    startTime: '',
    endTime: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (session && isOpen) {
      // Convert the session date to YYYY-MM-DD format for the date input using centralized utility
      let sessionDate = '';
      if (session.date) {
        try {
          // Use the centralized utility to get the Chicago date, then format for date input
          const chicagoDate = formatDateForDisplay(session.date, 'yyyy-MM-dd');
          sessionDate = chicagoDate;
        } catch (error) {
          console.error('Error formatting session date:', error);
          sessionDate = '';
        }
      }
      
      // Extract time from UTC datetime values and convert to Chicago timezone for display
      let startTime = '19:00';
      let endTime = '21:00';
      
      if (session.start_time) {
        try {
          // Handle both Date objects and ISO strings - convert UTC to Chicago timezone
          let chicagoDateTime;
          if (session.start_time instanceof Date) {
            chicagoDateTime = DateTime.fromJSDate(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
          } else {
            chicagoDateTime = DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
          }
          startTime = chicagoDateTime.toFormat('HH:mm');
        } catch (error) {
          console.error('Error parsing start_time:', error);
        }
      }
      
      if (session.end_time) {
        try {
          // Handle both Date objects and ISO strings - convert UTC to Chicago timezone
          let chicagoDateTime;
          if (session.end_time instanceof Date) {
            chicagoDateTime = DateTime.fromJSDate(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
          } else {
            chicagoDateTime = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
          }
          endTime = chicagoDateTime.toFormat('HH:mm');
        } catch (error) {
          console.error('Error parsing end_time:', error);
        }
      }
      
      setFormData({
        date: sessionDate,
        title: session.title || '',
        notes: session.notes || '',
        startTime: startTime,
        endTime: endTime
      });
      setError('');
    }
  }, [session, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const sessionData = {
        date: formData.date,
        title: formData.title,
        notes: formData.notes,
        startTime: formData.startTime,
        endTime: formData.endTime
      };

      const response = await apiService.updateAttendanceSession(session.id, sessionData);

      if (response.success) {
        onSessionUpdated();
        onClose();
      } else {
        setError(response.message || 'Failed to update session');
      }
    } catch (error) {
      console.error('Error updating session:', error);
      setError('Failed to update session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setLoading(false);
    onClose();
  };

  if (!session) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <FiEdit3 size={20} />
                Edit Attendance Session
              </ModalTitle>
              <CloseButton onClick={handleClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <TimezoneIndicator>
              <TimezoneText>
                All times are in Chicago timezone (America/Chicago)
              </TimezoneText>
            </TimezoneIndicator>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="date">Session Date</Label>
                <Input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="title">Session Title</Label>
                <Input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Week 2 - Aqeedah"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <TextArea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes for this session..."
                />
              </FormGroup>

              <TimeRow>
                <FormGroup>
                  <Label htmlFor="startTime">Start Time</Label>
                  <Input
                    type="time"
                    id="startTime"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="endTime">End Time</Label>
                  <Input
                    type="time"
                    id="endTime"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </TimeRow>


              {error && (
                <ErrorMessage>
                  {error}
                </ErrorMessage>
              )}

              <ButtonGroup>
                <Button
                  type="button"
                  className="secondary"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="primary"
                  disabled={loading}
                >
                  {loading ? <LoadingSpinner /> : <FiSave size={16} />}
                  {loading ? 'Updating...' : 'Update Session'}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default EditAttendanceModal;
